import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types & helpers
// ============================================================================

interface Review {
  name?: string | null;
  title?: string | null;
  rating?: string | number | null;
  text?: string | null;
  date?: string | null;
}

interface GalleryImage {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

/** "Jim Kowalski" -> "Jim K." */
function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

// Shown on the company theme (and any property without reviews) so the section
// always renders something matching the reference design.
const FALLBACK_TESTIMONIALS: Review[] = [
  {
    text:
      'What a wonderful experience from start to finish. The leasing staff are warm and caring making every effort to help you find your perfect apartment.',
    name: 'Jim K.',
  },
];

// How long each testimonial stays on screen before auto-advancing (ms).
const AUTO_SCROLL_MS = 2500;

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ts-chevron"
      aria-hidden="true"
    >
      {direction === 'left' ? (
        <polyline points="15 6 9 12 15 18" />
      ) : (
        <polyline points="9 6 15 12 9 18" />
      )}
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function Testimonials() {
  const rawReviews = useCollectionBinding<Review>('testimonials.items', {
    collection: 'reviews',
  });

  const usable = (rawReviews ?? []).filter((r) => hasValue(r.text));
  const testimonials = usable.length > 0 ? usable : FALLBACK_TESTIMONIALS;
  const count = testimonials.length;

  const bgImages = useCollectionBinding<GalleryImage>('testimonials.background', {
    collection: 'photo_gallery',
    defaultLimit: 1,
  });
  const bg = bgImages[0];
  const bgSrc = bg?.image ?? bg?.original_url ?? '';

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (dir: 1 | -1) => setActive((prev) => (prev + dir + count) % count);

  // "Read more": the quote is clamped to a few lines on mobile. `expanded`
  // shows the full text; `clamped` tracks whether the current quote actually
  // overflows the clamp (so the toggle only appears when it's needed — never on
  // desktop, where the clamp is off, or for short quotes).
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  // Collapse again whenever the visible testimonial changes.
  useEffect(() => {
    setExpanded(false);
  }, [active]);

  // Measure whether the collapsed quote is truncated. Re-measured on resize so
  // rotating the device or crossing the desktop breakpoint updates the toggle.
  useEffect(() => {
    const el = quoteRef.current;
    if (!el || expanded) return;
    const measure = () => setClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, expanded]);

  // Auto-advance through the testimonials. The timer resets whenever `active`
  // changes (so manual navigation restarts the countdown), pauses on
  // hover/focus or while a quote is expanded, and is disabled for a single item
  // or reduced-motion users.
  useEffect(() => {
    if (count <= 1 || paused || expanded) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, AUTO_SCROLL_MS);
    return () => window.clearInterval(id);
  }, [count, active, paused, expanded]);

  const current = testimonials[active] ?? testimonials[0];

  return (
    <section
      className="ts-section"
      aria-label="Testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Faded property photo behind the quote */}
      {bgSrc ? (
        <VibesitesImage
          url={bgSrc}
          alt=""
          aria-hidden="true"
          sizes="100vw"
          className="ts-bg"
        />
      ) : null}
      <div className="ts-wash" aria-hidden="true" />

      <div className="ts-inner">
        <RichTextContent path="testimonials.heading" as="h2" className="ts-heading" />

        <div className="ts-quote-wrap" key={active}>
          {hasValue(current?.text) ? (
            <p
              ref={quoteRef}
              className={`ts-quote${expanded ? ' ts-quote-expanded' : ''}`}
            >
              &ldquo;{current.text}&rdquo;
            </p>
          ) : null}
          {hasValue(current?.text) && (clamped || expanded) ? (
            <button
              type="button"
              className="ts-readmore"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Read less' : 'Read more'}
            </button>
          ) : null}
          {hasValue(current?.name) ? (
            <p className="ts-author">{abbreviateName(current.name!)}</p>
          ) : null}
        </div>

        {count > 1 ? (
          <div className="ts-controls">
            <button
              type="button"
              aria-label="Previous testimonial"
              onClick={() => go(-1)}
              className="ts-arrow ts-arrow-left"
            >
              <Chevron direction="left" />
            </button>
            <span className="ts-count" aria-hidden="true">
              {active + 1} / {count}
            </span>
            <button
              type="button"
              aria-label="Next testimonial"
              onClick={() => go(1)}
              className="ts-arrow ts-arrow-right"
            >
              <Chevron direction="right" />
            </button>
          </div>
        ) : null}
      </div>

      <style>{`
        .ts-section {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: var(--vibe-color-background, #ffffff);
          isolation: isolate;
        }
        .ts-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: -2;
        }
        .ts-wash {
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.82);
          z-index: -1;
        }

        .ts-inner {
          position: relative;
          max-width: 62rem;
          margin: 0 auto;
          padding: 2rem 1.25rem 2.25rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .ts-inner { padding: 6.5rem 5.5rem 7rem; }
        }

        .ts-heading {
          margin: 0 0 1.25rem;
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          font-weight: 400;
          font-size: 1.5rem;
          line-height: 1.1;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
        }
        @media (min-width: 768px) {
          .ts-heading { font-size: 2.5rem; margin: 0 0 2rem; }
        }

        .ts-quote-wrap {
          max-width: 44rem;
          margin: 0 auto;
          animation: ts-slide-in 0.5s ease both;
        }
        @keyframes ts-slide-in {
          from { opacity: 0; transform: translateX(2rem); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ts-quote-wrap { animation: none; }
        }
        .ts-quote {
          margin: 0;
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.9375rem;
          line-height: 1.6;
          color: var(--vibe-color-text, #342E1F);
          /* Keep any single review to a few lines on mobile so a long quote
             can't stretch the section — it rotates to the next one anyway. */
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 5;
          overflow: hidden;
        }
        .ts-quote-expanded {
          -webkit-line-clamp: unset;
          overflow: visible;
        }
        @media (min-width: 768px) {
          .ts-quote {
            font-size: 1.125rem;
            line-height: 1.9;
            display: block;
            overflow: visible;
            -webkit-line-clamp: none;
          }
        }

        /* "Read more" toggle — only rendered on mobile when the quote overflows
           its clamp. */
        .ts-readmore {
          margin-top: 0.75rem;
          padding: 0;
          border: none;
          background: transparent;
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.8125rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          text-decoration: underline;
          text-underline-offset: 0.2em;
          color: var(--vibe-color-secondary, #3E441D);
          cursor: pointer;
        }
        .ts-readmore:hover { opacity: 0.7; }
        .ts-author {
          margin: 1.25rem 0 0;
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 1rem;
          color: var(--vibe-color-secondary, #3E441D);
        }
        @media (min-width: 768px) {
          .ts-author { margin: 2rem 0 0; font-size: 1.125rem; }
        }

        /* Mobile: arrows + position indicator sit in a centered row below the
           quote, so the quote can use the full width instead of a narrow column. */
        .ts-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          margin-top: 1rem;
        }
        .ts-count {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 0.875rem;
          letter-spacing: 0.05em;
          color: var(--vibe-color-secondary, #3E441D);
          font-variant-numeric: tabular-nums;
        }
        .ts-arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          padding: 0;
          border: none;
          background: transparent;
          color: var(--vibe-color-secondary, #3E441D);
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .ts-arrow:hover { opacity: 0.6; }
        .ts-chevron { width: 1.75rem; height: 1.75rem; }

        /* Desktop: restore the arrows pinned to the sides of the quote. The
           controls wrapper collapses so the buttons position against .ts-inner. */
        @media (min-width: 768px) {
          .ts-controls { display: contents; }
          .ts-count { display: none; }
          .ts-arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
          }
          .ts-arrow-left { left: 1.5rem; }
          .ts-arrow-right { right: 1.5rem; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Testimonials',
  displayName: 'Testimonials',
  tokens: {
    'testimonials.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Testimonials</p>',
    },
  },
  collectionBindings: {
    'testimonials.items': {
      label: 'Testimonials',
      collection: 'reviews',
      description: 'Resident reviews shown in the rotating quote carousel.',
    },
    'testimonials.background': {
      label: 'Background Photo',
      collection: 'photo_gallery',
      defaultLimit: 1,
      description: 'Faded property photo shown behind the testimonial.',
    },
  },
};

(Testimonials as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Testimonials;
